import { reactive } from 'vue';

// An optional inline checkbox folded into the dialog (e.g. the leaver's
// "convert mailbox to shared" choice). Keeping it inside the confirm means a
// Cancel / Escape / backdrop dismiss genuinely aborts the whole action rather
// than being read as one of two affirmative answers.
export interface ConfirmCheckboxOption {
  label: string;
  default?: boolean;
}

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  // Style the confirm button as destructive (red).
  danger?: boolean;
  checkbox?: ConfirmCheckboxOption;
}

export interface ConfirmCheckboxResult {
  confirmed: boolean;
  checked: boolean;
}

interface ConfirmState {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  danger: boolean;
  checkbox: ConfirmCheckboxOption | null;
  checked: boolean;
}

let resolver: ((value: ConfirmCheckboxResult) => void) | null = null;

export const confirmState = reactive<ConfirmState>({
  open: false,
  title: '',
  message: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  danger: false,
  checkbox: null,
  checked: false,
});

// Promise-based replacement for window.confirm, rendered by <ConfirmDialog/>
// (mounted once in App.vue). Resolves true on confirm, false on cancel/dismiss.
// When a `checkbox` option is passed it resolves { confirmed, checked } instead,
// so the caller reads the checkbox only on an affirmative confirm.
export function confirm(opts: ConfirmOptions & { checkbox: ConfirmCheckboxOption }): Promise<ConfirmCheckboxResult>;
export function confirm(opts: ConfirmOptions): Promise<boolean>;
export function confirm(opts: ConfirmOptions): Promise<boolean | ConfirmCheckboxResult> {
  confirmState.title = opts.title;
  confirmState.message = opts.message ?? '';
  confirmState.confirmLabel = opts.confirmLabel ?? 'Confirm';
  confirmState.cancelLabel = opts.cancelLabel ?? 'Cancel';
  confirmState.danger = opts.danger ?? false;
  confirmState.checkbox = opts.checkbox ?? null;
  confirmState.checked = opts.checkbox?.default ?? false;
  confirmState.open = true;
  const hasCheckbox = Boolean(opts.checkbox);
  return new Promise((resolve) => {
    resolver = (value) => resolve(hasCheckbox ? value : value.confirmed);
  });
}

export function resolveConfirm(value: boolean): void {
  if (!confirmState.open) return;
  confirmState.open = false;
  const r = resolver;
  resolver = null;
  // A cancel/dismiss never reports the checkbox as ticked.
  r?.({ confirmed: value, checked: value ? confirmState.checked : false });
}
